import apiCall from './apiCall'
import { ApiHttpError } from './apiCall'
import type { ApiCallOptions } from './apiCall'
import { errorHandler } from 'utils/errorHandling'
import type {
    BreakpointTableView,
    CellHighlight,
    DebugFrameVariables,
    DebugStackView,
    RawTableView,
    StepInputsView,
    StepType,
    TraceParameterValue,
    TreeChildrenView,
    WatchView,
} from 'types/trace'

/**
 * Configuration for retry behavior.
 */
interface RetryConfig {
    /** Maximum number of retry attempts (default: 3) */
    maxAttempts?: number
    /** Initial delay in milliseconds before first retry (default: 500) */
    initialDelayMs?: number
    /** Maximum delay in milliseconds between retries (default: 5000) */
    maxDelayMs?: number
    /** Multiplier for exponential backoff (default: 2) */
    backoffMultiplier?: number
}

const DEFAULT_RETRY_CONFIG: Required<RetryConfig> = {
    maxAttempts: 3,
    initialDelayMs: 500,
    maxDelayMs: 5000,
    backoffMultiplier: 2,
}

/**
 * Determines if an error is transient and should be retried.
 * Transient errors include network failures and specific HTTP status codes.
 */
const isTransientError = (error: unknown): boolean => {
    if (error instanceof ApiHttpError) {
        return error.status === 502 || error.status === 503 || error.status === 504
    }

    if (error instanceof TypeError) {
        // Network errors like "Failed to fetch"
        return true
    }

    return false
}

/**
 * Delays execution for specified milliseconds.
 */
const delay = (ms: number): Promise<void> =>
    new Promise(resolve => setTimeout(resolve, ms))

/**
 * Executes an API call with configurable retry logic for transient errors.
 * Uses exponential backoff between retries.
 *
 * @param url API endpoint URL
 * @param params Optional fetch parameters
 * @param options API call options (throwError, suppressErrorPages)
 * @param retryConfig Optional retry configuration
 * @returns Promise resolving to the API response
 * @throws Re-throws the last error if all retries are exhausted
 */
const retryApiCall = async <T>(
    url: string,
    params?: RequestInit,
    options?: ApiCallOptions,
    retryConfig?: RetryConfig
): Promise<T> => {
    const config = { ...DEFAULT_RETRY_CONFIG, ...retryConfig }
    let lastError: unknown
    let currentDelay = config.initialDelayMs

    for (let attempt = 1; attempt <= config.maxAttempts; attempt++) {
        try {
            return await apiCall(url, params, options)
        } catch (error) {
            lastError = error

            // Log the failure
            if (error instanceof Error) {
                errorHandler.logError(error, {
                    message: `API call failed (attempt ${attempt}/${config.maxAttempts}): ${url}`,
                })
            }

            // Only retry on transient errors, and only if we have attempts left
            if (!isTransientError(error) || attempt >= config.maxAttempts) {
                break
            }

            // Wait before retrying with exponential backoff
            await delay(currentDelay)
            currentDelay = Math.min(currentDelay * config.backoffMultiplier, config.maxDelayMs)
        }
    }

    // All retries exhausted, rethrow the last error
    throw lastError
}

/** Standard API call options for trace endpoints */
// Tracing drives a debug session, not the project workspace, so its POSTs never signal a workspace change.
const TRACE_API_OPTIONS: ApiCallOptions = { throwError: true, suppressErrorPages: true, skipWorkspaceEvent: true }

const base = (projectId: string): string => `/projects/${encodeURIComponent(projectId)}/trace`

/**
 * API client for the interactive trace debugger.
 * All endpoints require project-level READ access and retry on transient network errors.
 */
export const traceService = {
    /**
     * Start a debug session. Returns the initial execution stack (suspended at entry or after
     * running to the first breakpoint).
     */
    startTrace: async (
        projectId: string,
        options: {
            tableId: string
            testRanges?: string
            fromModule?: string
            stopAtEntry?: boolean
            profiling?: boolean
            detailedTitles?: boolean
            breakOnErrors?: boolean
            includeTree?: boolean
            fullTree?: boolean
            inputJson?: string
        }
    ): Promise<DebugStackView> => {
        const params = new URLSearchParams()
        params.set('tableId', options.tableId)
        if (options.testRanges) params.set('testRanges', options.testRanges)
        if (options.fromModule) params.set('fromModule', options.fromModule)
        params.set('stopAtEntry', String(options.stopAtEntry ?? true))
        if (options.profiling) params.set('profiling', 'true')
        if (options.detailedTitles) params.set('detailedTitles', 'true')
        // The business view runs straight through a rule error so it still gets the whole executed tree with
        // the failed branch, instead of the debugger parking on the throwing frame.
        if (options.breakOnErrors === false) params.set('breakOnErrors', 'false')
        if (options.includeTree === false) params.set('includeTree', 'false')
        // The business view downloads the whole executed tree once, deep, instead of paging every branch.
        if (options.fullTree) params.set('fullTree', 'true')

        // Not retried: starting a session is not idempotent, so replaying a lost-response POST would spawn
        // and immediately discard a second worker. A transient failure surfaces to the caller instead.
        return await apiCall(
            `${base(projectId)}?${params.toString()}`,
            {
                method: 'POST',
                headers: options.inputJson ? { 'Content-Type': 'application/json' } : {},
                ...(options.inputJson && { body: options.inputJson }),
            },
            TRACE_API_OPTIONS
        )
    },

    /** Get the current execution stack. */
    getStack: async (projectId: string): Promise<DebugStackView> =>
        retryApiCall<DebugStackView>(`${base(projectId)}/stack`, undefined, TRACE_API_OPTIONS),

    /** Fetch one page of a step's executed sub-calls, for lazy loading of the executed tree. */
    getTreeChildren: async (
        projectId: string,
        uri: string,
        instance: number,
        step: string,
        offset: number,
        limit: number
    ): Promise<TreeChildrenView> => {
        const params = new URLSearchParams({
            uri,
            instance: String(instance),
            step,
            offset: String(offset),
            limit: String(limit),
        })
        return retryApiCall<TreeChildrenView>(
            `${base(projectId)}/tree/children?${params.toString()}`,
            undefined,
            TRACE_API_OPTIONS
        )
    },

    /**
     * Step the suspended session and return the new stack. Not retried: stepping is not idempotent, so
     * replaying a POST whose response was lost to a transient error would double-step the worker.
     */
    step: async (projectId: string, type: StepType): Promise<DebugStackView> =>
        apiCall(`${base(projectId)}/step?type=${type}`, { method: 'POST' }, TRACE_API_OPTIONS),

    /** Resume to the next breakpoint (asynchronous; outcome arrives via WebSocket). Not retried (not idempotent). */
    resume: async (projectId: string): Promise<void> =>
        apiCall(`${base(projectId)}/resume`, { method: 'POST' }, TRACE_API_OPTIONS),

    /** Request an asynchronous suspend at the next safepoint. Not retried (not idempotent). */
    pause: async (projectId: string): Promise<void> =>
        apiCall(`${base(projectId)}/pause`, { method: 'POST' }, TRACE_API_OPTIONS),

    /**
     * Export the full execution trace as plain text. Replays the run with a recording tracer and returns
     * the indented `TRACE:` tree with each node's value. When release is true the session is dropped once
     * the trace is produced.
     */
    exportTrace: async (projectId: string, smartNumbers: boolean, release = true): Promise<string> => {
        const params = new URLSearchParams()
        params.set('release', String(release))
        params.set('smartNumbers', String(smartNumbers))
        const text = await apiCall(`${base(projectId)}/export?${params.toString()}`, { method: 'GET' }, TRACE_API_OPTIONS)
        return typeof text === 'string' ? text : ''
    },

    /** Freeze and return a stack frame's variables (must be suspended). */
    getVariables: async (projectId: string, frameIndex: number): Promise<DebugFrameVariables> =>
        retryApiCall<DebugFrameVariables>(
            `${base(projectId)}/frames/${frameIndex}/variables`,
            undefined,
            TRACE_API_OPTIONS
        ),

    /**
     * Get the raw grid of a table (Tables API). The structure is immutable during a session, so the
     * client fetches it once per table and overlays per-step highlights on top.
     */
    getRawTable: async (
        projectId: string,
        tableId: string,
        maxRows?: number,
        styles?: boolean
    ): Promise<RawTableView> => {
        const cap = maxRows != null ? `&maxRows=${maxRows}` : ''
        const withStyles = styles ? '&styles=true' : ''
        return retryApiCall<RawTableView>(
            `/projects/${encodeURIComponent(projectId)}/tables/${encodeURIComponent(tableId)}?raw=true${cap}${withStyles}`,
            undefined,
            TRACE_API_OPTIONS
        )
    },

    /**
     * Get a focused spreadsheet step, self-contained: the values its formula consumed (named as the
     * formula writes them), the step's own returned value, and the A1 address of its cell. Everything the
     * step panel shows, so a step click need not also fetch the frame's full variables.
     */
    getStepInputs: async (projectId: string, frameIndex: number, ref: string): Promise<StepInputsView> =>
        retryApiCall<StepInputsView>(
            `${base(projectId)}/frames/${frameIndex}/step-inputs?ref=${encodeURIComponent(ref)}`,
            undefined,
            TRACE_API_OPTIONS
        ),

    /** Get the cells to highlight on a stack frame's table, keyed by A1 address. */
    getFrameHighlights: async (projectId: string, frameIndex: number): Promise<CellHighlight[]> =>
        retryApiCall<CellHighlight[]>(
            `${base(projectId)}/frames/${frameIndex}/highlights`,
            undefined,
            TRACE_API_OPTIONS
        ),

    /** Get a lazy-loaded parameter value. */
    getParameterValue: async (projectId: string, parameterId: number): Promise<TraceParameterValue> =>
        retryApiCall<TraceParameterValue>(
            `${base(projectId)}/parameters/${parameterId}`,
            undefined,
            TRACE_API_OPTIONS
        ),

    /** Get the breakpoints (table URIs). */
    getBreakpoints: async (projectId: string): Promise<string[]> =>
        retryApiCall<string[]>(`${base(projectId)}/breakpoints`, undefined, TRACE_API_OPTIONS),

    /** List rule tables that can be a breakpoint target, to set a breakpoint by name before it runs. */
    getBreakpointTables: async (projectId: string): Promise<BreakpointTableView[]> =>
        retryApiCall<BreakpointTableView[]>(
            // Only the name is needed to set and label a breakpoint; trim the rest with field projection.
            `${base(projectId)}/breakpoint-tables?fields=name`,
            undefined,
            TRACE_API_OPTIONS
        ),

    /** Replace the breakpoint set. */
    setBreakpoints: async (projectId: string, uris: string[]): Promise<void> =>
        retryApiCall<void>(
            `${base(projectId)}/breakpoints`,
            {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ uris }),
            },
            TRACE_API_OPTIONS
        ),

    /** The watched cells' values across the run, one series per cell. Complete once the run has finished. */
    getWatch: async (projectId: string): Promise<WatchView> =>
        retryApiCall<WatchView>(`${base(projectId)}/watch`, undefined, TRACE_API_OPTIONS),

    /** Replace the watch set. Applied on the next start, since a watch captures from the run's beginning. */
    setWatches: async (projectId: string, cells: string[]): Promise<void> =>
        retryApiCall<void>(
            `${base(projectId)}/watches`,
            {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ cells }),
            },
            TRACE_API_OPTIONS
        ),

    /** Terminate the debug session. Returns 204 on success, 404 if none. */
    cancelTrace: async (projectId: string): Promise<void> =>
        retryApiCall<void>(base(projectId), { method: 'DELETE' }, TRACE_API_OPTIONS),

    /**
     * Terminate the debug session as the debugger window closes. Fire-and-forget: keepalive lets the
     * request outlive the closing page, and its outcome (including a 404 when no session is left) is
     * irrelevant to a window that is already gone.
     */
    releaseOnClose: (projectId: string): void => {
        void apiCall(base(projectId), { method: 'DELETE', keepalive: true }, TRACE_API_OPTIONS)
            .catch(() => { /* the window is closing; there is nobody to report to */ })
    },
}

export default traceService
